import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { teamLabel } from "@alpha/shared";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useProfile } from "../lib/me";
import { tournamentPath } from "../lib/routes";
import { formatDateRange } from "../lib/format";
import { ProfileWizard } from "./ProfileWizard";
import type { HistoryGame, HistoryGroup, RegistrationListItem, Team } from "../lib/types";
import {
  Badge,
  ButtonLink,
  Card,
  EmptyState,
  LoadingBlock,
  PageHeader,
  PlusIcon,
  RegStatusBadge,
  Section,
  Skeleton,
  stagger,
  TournamentStatusBadge,
  TrophyIcon,
  SignInCard,
} from "../components/ui";

/** Signed-in landing (spec §8.5): my registrations + register CTA. */
export function Dashboard() {
  const { idToken, ready } = useAuth();

  const profileQ = useProfile();

  const regsQ = useQuery({
    queryKey: ["registrations"],
    enabled: !!idToken && !!profileQ.data,
    queryFn: () => api.get<{ registrations: RegistrationListItem[] }>("/me/registrations"),
  });

  // Team names for registration rows (shares the cache with My teams).
  const teamsQ = useQuery({
    queryKey: ["my-teams"],
    enabled: !!idToken && !!profileQ.data,
    queryFn: () => api.get<{ teams: Team[] }>("/me/teams"),
  });

  const historyQ = useQuery({
    queryKey: ["history"],
    enabled: !!idToken && !!profileQ.data,
    queryFn: () => api.get<{ history: HistoryGroup[] }>("/me/history"),
  });

  if (!ready) return <LoadingBlock />;
  if (!idToken) {
    return (
      <SignInCard
        title="Welcome back, coach"
        hint="Sign in to manage your teams and registrations."
        next="/me"
      />
    );
  }
  if (profileQ.isLoading) return <LoadingBlock label="Loading your profile…" />;
  if (profileQ.data === null) return <ProfileWizard />;

  const registrations = regsQ.data?.registrations ?? [];
  const teamLabelById = new Map((teamsQ.data?.teams ?? []).map((t) => [t.id, teamLabel(t)]));

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Dashboard"
        title={`Welcome, ${profileQ.data?.displayName}`}
        subtitle="Manage your teams and tournament registrations."
        actions={
          <>
            {/* Ghost, secondary, primary, left to right: the profile is the
                thing a coach touches once a season, and it sits here rather
                than in the nav because it belongs to the account, not beside
                Home and Tournaments. */}
            <ButtonLink variant="ghost" to="/me/profile">
              Edit profile
            </ButtonLink>
            <ButtonLink variant="secondary" to="/me/teams">
              My teams
            </ButtonLink>
            <ButtonLink to="/me/register">
              <PlusIcon className="h-4 w-4" />
              Register a team
            </ButtonLink>
          </>
        }
      />

      {/*
        The two things a coach comes here for — what they have entered, and how
        those teams have done — sit one above the other on a laptop and side by
        side from xl. Both rows are name-left, status-right, so stretching them
        across a wide screen would park every badge a hand's width from the team
        it belongs to. Two columns keep each row the width it was designed at.
      */}
      <div className="grid items-start gap-8 xl:grid-cols-2">
        <Section
          title="My registrations"
          badge={registrations.length > 0 ? <Badge>{registrations.length}</Badge> : undefined}
          className="animate-rise"
          style={stagger(2)}
        >
          {regsQ.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
            </div>
          ) : regsQ.isError ? (
            // Before the empty case, for the same reason "Games played" does it
            // below: a failed request has no registrations either, and falling
            // through would tell a coach who registered three teams that they
            // have none and invite them to start over.
            <EmptyState
              icon={<TrophyIcon className="h-5 w-5" />}
              title="Couldn't load your registrations"
              hint="Check your connection and try again."
            />
          ) : registrations.length === 0 ? (
            <EmptyState
              icon={<TrophyIcon className="h-5 w-5" />}
              title="No registrations yet"
              hint="Register one of your teams for an upcoming tournament."
              action={
                <ButtonLink to="/me/register" size="sm">
                  Register a team
                </ButtonLink>
              }
            />
          ) : (
            <div className="space-y-2">
              {registrations.map((r, i) => (
                <Card
                  key={r.id}
                  className="flex animate-rise items-center justify-between gap-3 p-4"
                  style={stagger(i)}
                >
                  <div className="min-w-0">
                    {r.tournament ? (
                      <Link
                        to={tournamentPath(r.tournament.id)}
                        className="block truncate font-medium text-navy-950 transition-colors hover:text-brandText"
                      >
                        {r.tournament.name}
                      </Link>
                    ) : (
                      <p className="truncate font-medium text-slate-400">Tournament unavailable</p>
                    )}
                    <p className="truncate text-sm text-slate-600">
                      {teamLabelById.get(r.teamId) ?? `Team ${r.teamId}`}
                    </p>
                    <p className="truncate text-xs text-slate-500">
                      {[
                        r.tournament
                          ? formatDateRange(r.tournament.startDate, r.tournament.endDate)
                          : null,
                        `Submitted ${new Date(r.submittedAt).toLocaleDateString()}`,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </div>
                  <RegStatusBadge status={r.status} />
                </Card>
              ))}
            </div>
          )}
        </Section>

        <Section
          title="Games played"
          badge={
            historyQ.data && historyQ.data.history.length > 0 ? (
              <Badge>{historyQ.data.history.length}</Badge>
            ) : undefined
          }
          className="animate-rise"
          style={stagger(3)}
        >
          {historyQ.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-24" />
              <Skeleton className="h-24" />
            </div>
          ) : historyQ.isError ? (
            // Checked BEFORE the empty case on purpose: a failed request has no
            // history either, and falling through would tell a coach with years
            // of games that they have none and invite them to register a team.
            <EmptyState
              icon={<TrophyIcon className="h-5 w-5" />}
              title="Couldn't load your games"
              hint="Check your connection and try again."
            />
          ) : (historyQ.data?.history.length ?? 0) === 0 ? (
            <EmptyState
              icon={<TrophyIcon className="h-5 w-5" />}
              title="No games yet"
              hint="Once your team is approved and the schedule is posted, your games show up here."
              action={
                <ButtonLink to="/me/register" size="sm">
                  Register a team
                </ButtonLink>
              }
            />
          ) : (
            <div className="space-y-4">
              {historyQ.data!.history.map((group, i) => (
                <Card key={group.tournament.id} className="animate-rise" style={stagger(i)}>
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 p-4">
                    <div className="min-w-0">
                      <Link
                        to={tournamentPath(group.tournament.id)}
                        className="truncate font-medium text-navy-950 transition-colors hover:text-brandText"
                      >
                        {group.tournament.name}
                      </Link>
                      <p className="text-xs text-slate-500">
                        {formatDateRange(group.tournament.startDate, group.tournament.endDate)}
                      </p>
                    </div>
                    <TournamentStatusBadge status={group.tournament.status} />
                  </div>
                  <div className="divide-y divide-slate-100">
                    {group.games.map((g) => (
                      <GameRow key={g.id} game={g} />
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}

const RESULT_TONE = {
  win: "green",
  loss: "red",
  tie: "slate",
  forfeit: "amber",
  scheduled: "slate",
  canceled: "slate",
} as const;

const RESULT_LABEL = {
  win: "W",
  loss: "L",
  tie: "T",
  forfeit: "FFT",
  scheduled: "—",
  canceled: "Canceled",
} as const;

function GameRow({ game }: { game: HistoryGame }) {
  const scored = game.teamScore !== undefined && game.opponentScore !== undefined;
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3">
      <div className="min-w-0">
        <p className="truncate text-sm text-navy-950">
          {game.teamName} <span className="text-slate-400">vs</span> {game.opponentName}
        </p>
        <p className="truncate text-xs text-slate-500">
          {[
            game.divisionName,
            game.scheduledAt ? new Date(game.scheduledAt).toLocaleDateString() : null,
            game.location,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {scored ? (
          <span className="text-sm font-semibold tabular-nums text-navy-950">
            {game.teamScore}–{game.opponentScore}
          </span>
        ) : null}
        <Badge tone={RESULT_TONE[game.result]}>{RESULT_LABEL[game.result]}</Badge>
      </div>
    </div>
  );
}
